/* eslint-disable jsx-a11y/media-has-caption */
import SubTitle from "components/SubTitle";
import React, { useEffect, useRef, useState } from "react";
import { Image, OverlayTrigger, Tooltip } from "react-bootstrap";
import ReactCrop, {
  Crop,
  PixelCrop,
  centerCrop,
  makeAspectCrop,
} from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import { Download, SkipForward, SkipBackward } from "react-bootstrap-icons";
import DownloadFile from "common/helpers/DownloadFile";
import { useTranslation } from "react-i18next";
import { renderTitle } from "../../reportAssets/assets";

type headerProps = {
  currStep: number;
  icon?: boolean;
  hasVideo?: boolean;
  hasImage?: boolean;
  src?;
  setCanvasImages?;
  currentCroppedButton?;
  setAddCroppedImageArray?: React.Dispatch<React.SetStateAction<any>>;
  reportOffenceStartTime: string;
};
const SrcWithTilte = ({
  currStep,
  icon,
  hasImage,
  hasVideo,
  src,
  setCanvasImages,
  currentCroppedButton,
  setAddCroppedImageArray,
  reportOffenceStartTime,
}: headerProps) => {
  const { t } = useTranslation();
  const [crop, setCrop] = useState<Crop>(null);
  const [videoElementDimensions, setVideoElementDimensions] = useState({
    width: 0,
    height: 0,
  });

  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasImages = {
    licenseNumberPicture: useRef(null),
    offencePicture: useRef(null),
    extraPicture: useRef(null),
    roadProblemPicture: useRef(null),
  };

  const [markerPosition, setMarkerPosition] = useState(0);
  const [isMarkerShow, setIsMarkerShow] = useState(false);
  const [isVideoShowSuccess, setIsVideoShowSuccess] = useState(false);

  useEffect(() => {
    if (currentCroppedButton) {
      const videoDimensions = videoRef.current;
      const { videoWidth, videoHeight } = videoDimensions;

      const cropTemplate = centerCrop(
        makeAspectCrop(
          {
            unit: "%",
            width: 80,
          },
          16 / 9,
          videoWidth,
          videoHeight
        ),
        videoWidth,
        videoHeight
      );

      setCrop(cropTemplate);
    } else {
      setCrop(null);
    }
  }, [currentCroppedButton]);

  const handleSeek = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    frames: number
  ) => {
    e.preventDefault();
    if (videoRef.current) {
      const frameRate = 30; // Change this to match your video's frame rate
      const frameDuration = 1 / frameRate;
      const newTime = videoRef.current.currentTime + frames * frameDuration;
      // Ensure the new time is within the video's duration
      const maxTime = videoRef.current.duration;
      const currentTime = Math.max(0, Math.min(newTime, maxTime));
      videoRef.current.currentTime = currentTime;
    }
  };

  const handleVideoPlay = (e, c: PixelCrop) => {
    if (!currentCroppedButton) {
      return;
    }
    if (e) {
      e.preventDefault();
    }

    const canvas = canvasImages[currentCroppedButton].current;
    const video = videoRef.current;
    if (canvas && video) {
      if (!canvas.height || !canvas.width) {
        return;
      }
      const scaleFactor = 4;
      canvas.width = (c?.width || video.videoWidth) * scaleFactor;
      canvas.height = (c?.height || video.videoHeight) * scaleFactor;

      const context = canvas.getContext("2d");
      if (context) {
        const widthRatio = video.videoWidth / videoElementDimensions.width;
        const heightRatio = video.videoHeight / videoElementDimensions.height;

        context.scale(scaleFactor, scaleFactor);

        context.drawImage(
          video,
          c.x * widthRatio,
          c.y * heightRatio,
          c.width * widthRatio,
          c.height * heightRatio,
          0,
          0,
          canvas.width / scaleFactor,
          canvas.height / scaleFactor
        );

        context.scale(1 / scaleFactor, 1 / scaleFactor);

        // Get the cropped image data as a pixel array
        const imageData = context.getImageData(
          0,
          0,
          canvas.width,
          canvas.height
        );

        // Create a new canvas and draw the pixel array onto it
        const newCanvas = document.createElement("canvas");
        newCanvas.width = canvas.width;
        newCanvas.height = canvas.height;
        const newContext = newCanvas.getContext("2d");
        newContext?.putImageData(imageData, 0, 0);

        // Get the base64-encoded image data from the new canvas
        const base64Image = newCanvas.toDataURL("image/png");

        setCanvasImages((prev) => ({
          ...prev,
          [currentCroppedButton]: canvasImages[currentCroppedButton],
        }));

        setAddCroppedImageArray((state) =>
          state.map((imageObject) => {
            if (imageObject.name === currentCroppedButton) {
              return {
                ...imageObject,
                src: base64Image,
              };
            }
            return { ...imageObject };
          })
        );
      }
    }
  };

  const handleMetadataLoaded = () => {
    setIsMarkerShow(true);
    setIsVideoShowSuccess(true);

    if (videoRef.current) {
      const { videoWidth, videoHeight } = videoRef.current;
      const aspectRatio = videoWidth / videoHeight;

      const containerWidth = videoRef.current.parentElement.clientWidth;
      const containerHeight = containerWidth / aspectRatio;

      setVideoElementDimensions({
        width: containerWidth,
        height: containerHeight,
      });

      const { duration } = videoRef.current;
      const padding = 16;
      const markerWidth = 5;
      const amountofseconds = duration + 1;
      const specificSecond = Number(reportOffenceStartTime);

      // Validate specificSecond to be within the valid range
      if (specificSecond >= 0 && specificSecond <= duration) {
        // width of video  - padding left - padding right / duration of movie
        const pixelsPerSecond =
          (containerWidth - 2 * padding) / amountofseconds;

        const rtlSecond = amountofseconds - specificSecond;

        // rtlSecond * pixelsPerSecond + padding - marker width
        const pixelsForMarker =
          rtlSecond * pixelsPerSecond + padding - markerWidth;

        // Update the position state
        setMarkerPosition(pixelsForMarker);
      }
    }
  };

  const handleVideoError = () => {
    setIsMarkerShow(false);
    setIsVideoShowSuccess(false);
  };

  const renderSrc = (): React.ReactNode => {
    let srcToreturn: React.ReactNode;
    if (hasVideo) {
      srcToreturn = (
        <>
          <ReactCrop
            disabled={!currentCroppedButton}
            crop={crop}
            onChange={(_, percentCrop) => {
              if (percentCrop.height > 0 && percentCrop.width > 0) {
                setCrop(percentCrop);
              }
            }}
            onComplete={(c) => {
              handleVideoPlay(null, c);
            }}
            style={{ zIndex: 100 }}
          >
            <div
              className="video-container"
              onMouseLeave={() =>
                !videoRef.current.paused && setIsMarkerShow(false)
              }
              onMouseEnter={() => isVideoShowSuccess && setIsMarkerShow(true)}
            >
              <video
                controls
                ref={videoRef}
                crossOrigin="anonymous"
                onLoadedMetadata={handleMetadataLoaded}
                onClick={(e) => e.preventDefault()}
                onError={handleVideoError}
              >
                <source src={src} type="video/mp4" />
              </video>

              <OverlayTrigger
                placement="top"
                overlay={
                  <Tooltip style={{ fontSize: "14px" }}>
                    {t("offenceTime")}
                  </Tooltip>
                }
              >
                <div
                  className="fixed-point-marker"
                  style={{
                    right: `${markerPosition}px`,
                    display: isMarkerShow ? "block" : "none",
                  }}
                />
              </OverlayTrigger>
            </div>
          </ReactCrop>
          <div className="video-controls">
            <button
              type="button"
              className="seek-button"
              onClick={(e) => handleSeek(e, 1)}
              disabled={
                !(videoRef.current && videoRef.current.readyState !== 0)
              }
            >
              <SkipForward className="icon-directional" /> {t("nextFrame")}
            </button>
            <button
              type="button"
              className="seek-button"
              onClick={(e) => handleSeek(e, -1)}
              disabled={
                !(videoRef.current && videoRef.current.readyState !== 0)
              }
            >
              {t("previousFrame")} <SkipBackward className="icon-directional" />
            </button>
          </div>
          <div className="canvas-container" style={{ display: "none" }}>
            <canvas ref={canvasImages.licenseNumberPicture} />
            <canvas ref={canvasImages.offencePicture} />
            <canvas ref={canvasImages.extraPicture} />
            <canvas ref={canvasImages.roadProblemPicture} />
          </div>
        </>
      );
    }
    if (hasImage) {
      srcToreturn = (
        <Image
          src={src}
          alt={src}
          height={300}
          width="100%"
          className="google-map-report"
          onDragStart={(e) => {
            e.preventDefault();
          }}
        />
      );
    }
    return srcToreturn;
  };
  return (
    <section className="header-report-details-step-section">
      <div className="felx-container">
        <SubTitle className="default-vertical-padding">
          {renderTitle[currStep - 1]}
        </SubTitle>

        {icon && (
          <button type="button" onClick={() => DownloadFile(src)}>
            <Download size={20} />
          </button>
        )}
      </div>

      <div className="image-or-video-container">{renderSrc()}</div>
    </section>
  );
};
export default SrcWithTilte;
